import {
  type AccessibilityFrame,
  type AccessibleDisplayObject,
  type FocusHandlers,
  type ZimAccessibility,
  createAccessibilityFrame,
  getBoundsToGlobal,
  loadZimPreservingCreatejs,
} from './accessibilityFrame';

export type TabOrderEntry = {
  obj: createjs.DisplayObject;
  title: string;
  disabled: boolean;
  /**
   * Announced by a screen reader but not actionable (e.g. a value display).
   * Focusable so it can be navigated to, but marked so it is not read as a
   * clickable control.
   */
  readOnly?: boolean;
};

export type CanvasAccessibilityOptions = {
  appName: string;
  stage: createjs.Stage;
  getTabOrder: () => TabOrderEntry[];
  getFallbackBounds?: (obj: createjs.DisplayObject) => createjs.Rectangle;
  onEntryFocus?: (obj: createjs.DisplayObject) => void;
  onEntryBlur?: (obj: createjs.DisplayObject) => void;
  /**
   * Draw a generic rounded-rectangle outline+glow around the focused entry.
   * Intended for interfaces without their own hover/focus animation.
   */
  focusHighlight?: boolean;
};

export type CanvasAccessibility = {
  rebuild(): void;
  dispose(): void;
};

export async function createCanvasAccessibility(
  options: CanvasAccessibilityOptions,
): Promise<CanvasAccessibility | null> {
  const {
    appName,
    focusHighlight,
    getFallbackBounds,
    getTabOrder,
    onEntryBlur,
    onEntryFocus,
    stage,
  } = options;

  const initialTabOrder = getTabOrder();
  if (initialTabOrder.length === 0) return null;

  // zimjs imports its own CreateJS fork and assigns it to window.createjs.
  // Preserve the runtime that created this stage and restore it immediately
  // so the caller does not mix objects from two CreateJS namespaces.
  const appCreatejs = createjs;
  const zim = await loadZimPreservingCreatejs(appCreatejs);

  const {
    frame,
    dispose: disposeFrame,
    updateGeometry,
  } = createAccessibilityFrame(stage, 'canvas-a11y');
  const canvas = stage.canvas as HTMLCanvasElement;
  const adaptedObjects = new Map<AccessibleDisplayObject, unknown>();
  const focusHandlers = new Map<HTMLElement, FocusHandlers>();
  const highlight = focusHighlight ? createFocusHighlight(stage) : null;
  const handleEntryFocus = highlight
    ? (obj: createjs.DisplayObject): void => {
        highlight.onFocus(obj);
        onEntryFocus?.(obj);
      }
    : onEntryFocus;
  const handleEntryBlur = highlight
    ? (obj: createjs.DisplayObject): void => {
        highlight.onBlur(obj);
        onEntryBlur?.(obj);
      }
    : onEntryBlur;
  const AccessibilityCtor = zim.Accessibility as unknown as new (config: {
    appName: string;
    frame: AccessibilityFrame;
    tabOrder: TabOrderEntry[];
  }) => ZimAccessibility<TabOrderEntry>;

  let accessibility: ZimAccessibility<TabOrderEntry>;
  try {
    prepareTabOrder(initialTabOrder, adaptedObjects, getFallbackBounds);
    accessibility = new AccessibilityCtor({
      appName,
      frame,
      tabOrder: initialTabOrder,
    });
    applyDisabledState(initialTabOrder);
    disableBoundaryTabStops(canvas, initialTabOrder);
    applyFocusHandlers(
      initialTabOrder,
      focusHandlers,
      accessibility,
      handleEntryFocus,
      handleEntryBlur,
    );
  } catch (error) {
    clearFocusHandlers(focusHandlers, handleEntryBlur);
    highlight?.dispose();
    disposeFrame();
    restoreAdaptedObjects(adaptedObjects);
    throw error;
  }

  function rebuild(): void {
    const focusedObject = focusHandlers.get(
      document.activeElement as HTMLElement,
    )?.obj;
    const tabOrder = getTabOrder();
    prepareTabOrder(tabOrder, adaptedObjects, getFallbackBounds);
    clearFocusHandlers(focusHandlers, handleEntryBlur);
    accessibility.tabOrder = tabOrder;
    applyDisabledState(tabOrder);
    disableBoundaryTabStops(canvas, tabOrder);
    updateGeometry();
    accessibility.resize();
    applyFocusHandlers(
      tabOrder,
      focusHandlers,
      accessibility,
      handleEntryFocus,
      handleEntryBlur,
    );

    if (focusedObject) {
      const focusTarget = tabOrder.some(({ obj }) => obj === focusedObject)
        ? focusedObject
        : tabOrder.find(({ disabled }) => !disabled)?.obj;
      if (focusTarget) {
        (focusTarget as AccessibleDisplayObject).zimTabTag?.focus();
      } else {
        accessibility.tabIndex = -1;
      }
    }
  }

  function onResize(): void {
    updateGeometry();
    accessibility.resize();
  }

  window.addEventListener('resize', onResize);

  return {
    rebuild,
    dispose(): void {
      window.removeEventListener('resize', onResize);
      clearFocusHandlers(focusHandlers, handleEntryBlur);
      highlight?.dispose();
      accessibility.dispose();
      disposeFrame();
      restoreAdaptedObjects(adaptedObjects);
    },
  };
}

function prepareTabOrder(
  tabOrder: TabOrderEntry[],
  adaptedObjects: Map<AccessibleDisplayObject, unknown>,
  getFallbackBounds:
    | ((obj: createjs.DisplayObject) => createjs.Rectangle)
    | undefined,
): void {
  for (const { obj } of tabOrder) {
    const accessibleObject = obj as AccessibleDisplayObject;
    if (adaptedObjects.has(accessibleObject)) continue;

    adaptedObjects.set(accessibleObject, accessibleObject.boundsToGlobal);
    // Recompute bounds on each call so focus geometry tracks later layout/scale changes.
    accessibleObject.boundsToGlobal = () =>
      getBoundsToGlobal(
        accessibleObject,
        getDisplayObjectBounds(accessibleObject, getFallbackBounds),
      );
  }
}

function getDisplayObjectBounds(
  obj: createjs.DisplayObject,
  getFallbackBounds:
    | ((obj: createjs.DisplayObject) => createjs.Rectangle)
    | undefined,
): createjs.Rectangle {
  return (
    (obj.getBounds as () => createjs.Rectangle | null)() ??
    getFallbackBounds?.(obj) ??
    new createjs.Rectangle(0, 0, 0, 0)
  );
}

function applyDisabledState(tabOrder: TabOrderEntry[]): void {
  for (const entry of tabOrder) {
    const tag = (entry.obj as AccessibleDisplayObject).zimTabTag;
    if (!tag) continue;
    tag.setAttribute('aria-disabled', String(entry.disabled));
    if (entry.readOnly) {
      // Override ZIM's button semantics so it is announced as static text.
      tag.setAttribute('role', 'note');
    }
  }
}

function disableBoundaryTabStops(
  canvas: HTMLCanvasElement,
  tabOrder: TabOrderEntry[],
): void {
  // ZIM always creates offscreen boundary tags for its internal Tab loop. This
  // adapter uses the generated node tags in the browser's natural tab order, so
  // the boundary tags must not add invisible stops before and after the canvas.
  const nodeTags = new Set(
    tabOrder
      .map(({ obj }) => (obj as AccessibleDisplayObject).zimTabTag)
      .filter((tag): tag is HTMLElement => !!tag),
  );

  for (const element of canvas.parentElement?.children ?? []) {
    if (
      element instanceof HTMLElement &&
      element !== canvas &&
      element.id.startsWith(canvas.id) &&
      !nodeTags.has(element)
    ) {
      element.tabIndex = -1;
      element.setAttribute('aria-hidden', 'true');
    }
  }
}

function applyFocusHandlers(
  tabOrder: TabOrderEntry[],
  focusHandlers: Map<HTMLElement, FocusHandlers>,
  accessibility: ZimAccessibility<TabOrderEntry>,
  onEntryFocus: ((obj: createjs.DisplayObject) => void) | undefined,
  onEntryBlur: ((obj: createjs.DisplayObject) => void) | undefined,
): void {
  for (const entry of tabOrder) {
    const tag = (entry.obj as AccessibleDisplayObject).zimTabTag;
    if (!tag) continue;

    function onFocus(): void {
      onEntryFocus?.(entry.obj);
    }
    function onBlur(event: FocusEvent): void {
      onEntryBlur?.(entry.obj);
      // Clear ZIM's internal selection when focus leaves the canvas tab order so
      // it does not restore a stale entry on the next interaction.
      if (
        !(event.relatedTarget instanceof HTMLElement) ||
        !focusHandlers.has(event.relatedTarget)
      ) {
        accessibility.tabIndex = -1;
      }
    }
    function onKeyDown(event: KeyboardEvent): void {
      if ((event.key !== 'Enter' && event.key !== ' ') || event.repeat) return;

      event.preventDefault();
      // Read-only entries are focusable/announced but not actionable.
      if (entry.disabled || entry.readOnly) return;
      entry.obj.dispatchEvent(new createjs.Event('click', false, false));
    }
    tag.addEventListener('focus', onFocus);
    tag.addEventListener('blur', onBlur);
    tag.addEventListener('keydown', onKeyDown);
    focusHandlers.set(tag, {
      obj: entry.obj,
      onBlur,
      onFocus,
      onKeyDown,
    });
  }
}

function clearFocusHandlers(
  focusHandlers: Map<HTMLElement, FocusHandlers>,
  onEntryBlur: ((obj: createjs.DisplayObject) => void) | undefined,
): void {
  for (const [tag, handlers] of focusHandlers) {
    if (document.activeElement === tag) {
      onEntryBlur?.(handlers.obj);
    }
    tag.removeEventListener('focus', handlers.onFocus);
    tag.removeEventListener('blur', handlers.onBlur);
    tag.removeEventListener('keydown', handlers.onKeyDown);
  }
  focusHandlers.clear();
}

function restoreAdaptedObjects(
  adaptedObjects: Map<AccessibleDisplayObject, unknown>,
): void {
  for (const [obj, previousBoundsToGlobal] of adaptedObjects) {
    if (previousBoundsToGlobal === undefined) {
      delete obj.boundsToGlobal;
    } else {
      obj.boundsToGlobal = previousBoundsToGlobal as () => createjs.Rectangle;
    }
  }
}

const HIGHLIGHT_DEFAULTS = {
  color: '#3fb2ff',
  thickness: 4,
  padding: 6,
  cornerRadius: 10,
  glowColor: '#ffffff',
  glowBlur: 12,
};

/**
 * Generic focus indicator: a rounded-rectangle outline with a soft glow that
 * tracks the focused entry every tick, so it stays aligned even while the
 * focused element animates or moves.
 */
function createFocusHighlight(stage: createjs.Stage): {
  onFocus: (obj: createjs.DisplayObject) => void;
  onBlur: (obj: createjs.DisplayObject) => void;
  dispose: () => void;
} {
  const style = HIGHLIGHT_DEFAULTS;
  const overlay = new createjs.Shape();
  overlay.mouseEnabled = false;
  overlay.shadow = new createjs.Shadow(style.glowColor, 0, 0, style.glowBlur);
  stage.addChild(overlay);

  let focused: createjs.DisplayObject | null = null;
  let listening = false;
  // Track input modality so the indicator only appears for keyboard focus
  // (like :focus-visible) and not for the focus that follows a mouse click.
  let keyboardModality = false;

  function onKeyDown(): void {
    keyboardModality = true;
  }
  function onPointerDown(): void {
    keyboardModality = false;
  }

  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('pointerdown', onPointerDown, true);
  window.addEventListener('mousedown', onPointerDown, true);
  window.addEventListener('touchstart', onPointerDown, true);

  function draw(): void {
    const graphics = overlay.graphics.clear();
    if (!focused) return;
    const bounds = (focused as AccessibleDisplayObject).boundsToGlobal?.();
    if (!bounds) return;
    // The overlay lives inside the (scaled) stage, so map global bounds back to its local space.
    const topLeft = overlay.globalToLocal(bounds.x, bounds.y);
    const bottomRight = overlay.globalToLocal(
      bounds.x + bounds.width,
      bounds.y + bounds.height,
    );
    graphics
      .setStrokeStyle(style.thickness)
      .beginStroke(style.color)
      .drawRoundRect(
        topLeft.x - style.padding,
        topLeft.y - style.padding,
        bottomRight.x - topLeft.x + style.padding * 2,
        bottomRight.y - topLeft.y + style.padding * 2,
        style.cornerRadius,
      );
  }

  function onFocus(obj: createjs.DisplayObject): void {
    if (!keyboardModality) return;
    focused = obj;
    if (overlay.parent === stage) {
      stage.setChildIndex(overlay, stage.numChildren - 1);
    }
    if (!listening) {
      createjs.Ticker.addEventListener('tick', draw);
      listening = true;
    }
    draw();
  }

  function stopTracking(): void {
    focused = null;
    if (listening) {
      createjs.Ticker.removeEventListener('tick', draw);
      listening = false;
    }
    overlay.graphics.clear();
  }

  function onBlur(obj: createjs.DisplayObject): void {
    if (focused === obj) stopTracking();
  }

  function dispose(): void {
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('pointerdown', onPointerDown, true);
    window.removeEventListener('mousedown', onPointerDown, true);
    window.removeEventListener('touchstart', onPointerDown, true);
    stopTracking();
    stage.removeChild(overlay);
  }

  return { onFocus, onBlur, dispose };
}
